import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import type { SubjectCard } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";
import { WorldViewActivityLine } from "@/components/WorldViewActivityLine";

/**
 * UI-16's **World view** — the same index UI-10 draws, with the machinery taken off.
 *
 * Same read, same subjects, same order. What changes is what a reader is asked to do with
 * it: the navigator panel's six type filters are gone (there is no panel in simple mode),
 * the counts of anything but beliefs are gone, and the only control is a box for finding a
 * subject by name — because the reduction is about what somebody has to understand, not
 * about what they are allowed to see.
 *
 * The "things waiting" pill is the one place the two simple destinations meet. It is drawn
 * only when there is something behind it: a pill promising nothing is a pill that teaches
 * people not to read pills.
 */
export function SimpleWorldView() {
  const { worldView, worldViewLoading, worldViewProblem, refreshWorldView, worldViewActivity, reviewCounts } =
    useApp();
  const [filter, setFilter] = useState("");

  // Re-read on arrival rather than poll, the same rule the full index follows: synthesis
  // finishes on whichever machine the Reasoner is placed on and nothing tells this browser
  // when, so the moment somebody comes to look is the moment worth asking.
  useEffect(() => refreshWorldView(), [refreshWorldView]);

  const subjects = worldView?.subjects ?? [];
  const total = subjects.length;

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === "") return subjects;

    return subjects.filter((subject) =>
      `${subject.title} ${subject.kind} ${subject.summary ?? ""}`.toLowerCase().includes(needle),
    );
  }, [subjects, filter]);

  const waiting = reviewCounts?.needsYou ?? 0;

  return (
    <div className="mx-auto flex max-w-[1000px] flex-col gap-[var(--space-8)] px-[var(--space-8)] py-[var(--space-12)]">
      <header className="flex flex-col gap-2.5">
        <h1 className="text-[40px] font-extrabold leading-[1.1] tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          World view
        </h1>
        <p className="max-w-[58ch] text-[length:var(--text-lg)] leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]">
          Everything Factorium currently believes, written from what you brought in.
        </p>

        {!worldViewLoading && total > 0 && (
          <p className="flex flex-wrap items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {total} {total === 1 ? "subject" : "subjects"} ·{" "}
            {worldViewActivity ? (
              <WorldViewActivityLine activity={worldViewActivity} />
            ) : worldView?.lastRewrittenAt ? (
              `last rewritten ${formatRelative(worldView.lastRewrittenAt)}`
            ) : (
              "not rewritten yet"
            )}
          </p>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Find a subject…"
          aria-label="Find a subject"
          className="h-9 w-full min-w-[240px] max-w-[420px] flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--vl-bridge)]"
        />

        <span className="flex-1" />

        {waiting > 0 && (
          <Link
            to="/review"
            className="inline-flex items-center gap-2 rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-1.5 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
          >
            <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--warning)]" />
            {waiting === 1 ? "1 thing waiting" : `${waiting} things waiting`}
          </Link>
        )}
      </div>

      {worldViewProblem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {worldViewProblem}
        </p>
      )}

      {worldViewLoading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : shown.length === 0 ? (
        <Empty filtered={total > 0} />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-[var(--space-4)]">
          {shown.map((subject) => (
            <Card key={subject.id} subject={subject} />
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ subject }: { subject: SubjectCard }) {
  const meta = subjectKindMeta(subject.kind);
  const Icon = meta.icon;

  return (
    <Link
      to={`/world-view/${subject.slug}`}
      className="flex flex-col gap-2 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] shadow-[var(--shadow-sm)] backdrop-blur-[var(--blur-sm)] transition hover:-translate-y-0.5 hover:border-[var(--border-default)] hover:shadow-[var(--shadow-md)]"
    >
      <span className="flex items-center gap-1.5 text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        <Icon size={12} strokeWidth={2.5} className="text-[var(--vl-virtual-soft)]" />
        {meta.label}
      </span>

      <span className="text-[length:var(--text-xl)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)] [text-wrap:pretty]">
        {subject.title}
      </span>

      {subject.summary && (
        <span className="line-clamp-3 text-[length:var(--text-sm)] leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]">
          {subject.summary}
        </span>
      )}

      <span className="mt-1 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
        {subject.beliefCount} {subject.beliefCount === 1 ? "belief" : "beliefs"} · updated{" "}
        {formatRelative(subject.updatedAt)}
      </span>
    </Link>
  );
}

/**
 * Two different nothings, said differently — the same distinction the full index makes.
 *
 * The empty-world sentence is the one that changes in this mode: full mode sends somebody
 * to the review queue "to approve insights", which is a sentence about the pipeline. Here
 * it points at the two things this surface actually offers.
 */
function Empty({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <p className="px-[var(--space-6)] py-[var(--space-12)] text-center text-[length:var(--text-sm)] text-[var(--text-muted)]">
        No subject matches that yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-12)] text-center">
      <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Nothing is written yet</p>
      <p className="max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)] [text-wrap:pretty]">
        Add a meeting, a document or a note, answer what Factorium cannot decide on its own, and what you
        believe appears here.
      </p>
      <Link
        to="/add"
        className="mt-1 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110"
      >
        Add a source
      </Link>
    </div>
  );
}
